import React from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import { fmtInt, fmtMoney, fmtPct } from '../../utils/format';
import { GLOSARIO } from '../../utils/glosario';
import { lineaProcedenciaClientes } from '../../utils/datosCierre';
import {
  filaManejaCredito,
  muestraAviso7C03,
  procedenciaDelAmbito,
  TotalesMeta,
} from '../../utils/meta';
import type { MetaFila, MetaGeneraciones } from '../../types/indicadores.types';
import { NOTA_CLIENTES_GLOBAL } from '../VentaCards';

export interface ComposicionVentaProps {
  /** Los de `totalizarMeta` sobre las filas VISIBLES: nunca los totales de la API. */
  totales: TotalesMeta;
  generaciones: MetaGeneraciones;
  rango: { desde: string; hasta: string };
  /** Las filas visibles: deciden si hay TRE en el ámbito y si va el aviso 7C03. */
  filas: MetaFila[];
  /**
   * `false` cuando la cascada client-side acota (regional, supervisor, varias
   * tiendas): la deriva y «el correo dijo» de la API son del ámbito completo y
   * no se suman a las altas visibles. Por omisión, `true`.
   */
  ambitoCompleto?: boolean;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** `2026-10-24` -> `24-oct`, como se escribe en el resto de las notas. */
function diaMes(iso: string): string {
  const [, mm, dd] = iso.split('-').map(Number);
  return `${dd}-${MESES[mm - 1] ?? ''}`;
}

const TEXTO_ESTADO: Record<'pendiente' | 'sin-snapshot', string> = {
  pendiente:
    'Altas y estrenos: la consulta no respondió a tiempo; sigue en segundo plano. Vuelve a aplicar el periodo en unos minutos.',
  'sin-snapshot':
    'Altas y estrenos: el periodo no está materializado; esta vista no consulta el ERP para eso.',
};

/**
 * Chip gris de «sin meta». Gris a propósito: estas cifras no tienen contra qué
 * medirse todavía, y un semáforo —o un «0 %»— diría que van mal cuando lo que
 * pasa es que nadie ha cargado la meta.
 */
const ChipSinMeta: React.FC = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: 999,
      background: colors.chipBg,
      border: `1px solid ${colors.border}`,
      color: colors.texto.tertiary,
      fontSize: 11,
      fontWeight: 700,
      padding: '1px 8px',
      whiteSpace: 'nowrap',
    }}
  >
    meta: sin dato
  </span>
);

/** Punto de color de la serie, el mismo de la barra. */
const Punto: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden
    style={{
      width: 10,
      height: 10,
      borderRadius: 3,
      background: color,
      flex: 'none',
      alignSelf: 'center',
    }}
  />
);

/**
 * «Composición de la venta» del tablero de Presupuesto (fase 1, 27-oct-2026).
 *
 * Responde «¿de qué está hecha la venta?» —contado, crédito y, aparte, los
 * préstamos, que NO son venta— y «¿cuántos créditos nuevos salieron?» (altas y
 * estrenos). Nada de esto tiene meta todavía, así que NUNCA lleva semáforo: las
 * cifras van en el color de texto normal y con el chip «meta: sin dato».
 *
 * Suma TODAS las tiendas del ámbito, también las que no tienen meta: la venta
 * de VAZ 250 es venta a crédito real aunque no entre al cumplimiento.
 *
 * El «% de crédito sin TRE» existe porque TRE no maneja crédito: con su venta
 * en el denominador, el porcentaje de una cadena o un regional con tiendas TRE
 * se lee más bajo de lo que es para las tiendas que sí venden a crédito.
 */
const ComposicionVenta: React.FC<ComposicionVentaProps> = ({
  totales,
  generaciones,
  rango,
  filas,
  ambitoCompleto = true,
}) => {
  const esMovil = useEsMovil();

  const hayCredito = filas.length === 0 || filas.some(filaManejaCredito);
  const soloTre = !hayCredito;
  const conTre = totales.tiendasSinCredito > 0 && totales.tiendasConCredito > 0;
  const venta = totales.ventaTotalMonto;
  const pctContado = venta > 0 ? totales.contadoMonto / venta : null;
  const pctCredito = totales.creditoSobreVenta;

  const estadoOk = generaciones.estado === 'ok';
  const conteo = (n: number | null) => (estadoOk && !soloTre ? fmtInt(n) : '—');
  const lineaProcedencia =
    estadoOk && !soloTre
      ? lineaProcedenciaClientes(
          procedenciaDelAmbito(generaciones.procedencia, ambitoCompleto),
          totales.altas,
          rango,
        )
      : null;
  const avisoDesde =
    muestraAviso7C03(generaciones, filas) && generaciones.avisoCuentas7C03
      ? generaciones.avisoCuentas7C03.desde
      : null;

  const prestamoTickets =
    totales.prestamoTickets !== null && totales.prestamoTickets > 0
      ? ` (${fmtInt(totales.prestamoTickets)} ${totales.prestamoTickets === 1 ? 'ticket' : 'tickets'})`
      : '';

  const renglon: React.CSSProperties = {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 8,
    rowGap: 4,
    minWidth: 0,
    fontSize: esMovil ? 13 : 13.5,
    color: colors.texto.body,
    lineHeight: 1.4,
  };
  const rotulo: React.CSSProperties = { fontWeight: 700, color: colors.texto.primary };
  const cifra: React.CSSProperties = {
    fontWeight: 800,
    color: colors.texto.primary,
    fontVariantNumeric: 'tabular-nums',
  };
  const tenue: React.CSSProperties = { color: colors.texto.secondary };
  const subtitulo: React.CSSProperties = {
    fontSize: 12,
    color: colors.texto.tertiary,
    lineHeight: 1.4,
  };

  return (
    <section
      data-testid="composicion-venta"
      aria-label="Composición de la venta"
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 18,
        padding: esMovil ? '14px 12px' : '18px 22px',
        boxShadow: '0 1px 2px rgba(20,28,54,.05)',
        minWidth: 0,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: colors.texto.primary }}>
        Composición de la venta
      </h3>
      <div style={{ ...subtitulo, marginTop: 2 }}>
        sin IVA · todas las tiendas del ámbito
        {totales.ventaSinMeta > 0 && ` · incluye ${fmtMoney(totales.ventaSinMeta)} de tiendas sin meta`}
      </div>

      <div
        data-testid="composicion-cuerpo"
        style={{
          display: 'grid',
          // En escritorio, la venta a la izquierda y los créditos nuevos a la
          // derecha; en teléfono, una sola columna (regla 2 de la lección del
          // 29-ago: filas de tarjetas -> una columna).
          gridTemplateColumns: esMovil ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(0, 2fr)',
          gap: esMovil ? 14 : 24,
          marginTop: 14,
        }}
      >
        {/* --- La venta: contado + crédito, y los préstamos aparte --- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          <div
            role="img"
            aria-label={
              pctContado === null
                ? 'Sin venta en el periodo'
                : `Contado ${fmtPct(pctContado)}, crédito ${fmtPct(pctCredito)}`
            }
            style={{
              display: 'flex',
              height: 12,
              borderRadius: 999,
              overflow: 'hidden',
              background: colors.ringTrack,
              width: '100%',
            }}
          >
            {pctContado !== null && (
              <>
                <div style={{ width: `${pctContado * 100}%`, background: colors.serie.contado }} />
                <div style={{ width: `${(pctCredito ?? 0) * 100}%`, background: colors.serie.credito }} />
              </>
            )}
          </div>

          <div data-testid="composicion-contado" style={renglon}>
            <Punto color={colors.serie.contado} />
            <span style={rotulo}>Contado</span>{' '}
            <span style={cifra}>{fmtMoney(totales.contadoMonto)}</span>{' '}
            <span style={tenue}>· {fmtPct(pctContado)}</span>
          </div>

          <div data-testid="composicion-credito" style={renglon}>
            <Punto color={colors.serie.credito} />
            <span style={rotulo}>Crédito</span>{' '}
            {soloTre ? (
              <>
                <span style={cifra}>—</span> <span style={tenue}>· TRE no maneja crédito</span>
              </>
            ) : (
              <>
                <span style={cifra}>{fmtMoney(totales.creditoMonto)}</span>{' '}
                <span style={tenue}>
                  · {fmtPct(pctCredito)} de la venta
                  {conTre && ` · ${fmtPct(totales.creditoSobreVentaConCredito)} sin TRE`}
                </span>{' '}
                <ChipSinMeta />
              </>
            )}
          </div>

          <div data-testid="composicion-prestamos" style={renglon}>
            <Punto color={colors.texto.muted} />
            <span style={rotulo}>{GLOSARIO.prestamos.rotulo}</span>{' '}
            <span style={cifra}>
              {fmtMoney(totales.prestamoMonto)}
              {prestamoTickets}
            </span>{' '}
            <span style={tenue}>· {GLOSARIO.prestamos.nota}</span>
          </div>
        </div>

        {/* --- Créditos nuevos: altas y estrenos --- */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minWidth: 0,
            // Separador: arriba en teléfono, a la izquierda en escritorio.
            ...(esMovil
              ? { borderTop: `1px solid ${colors.border}`, paddingTop: 12 }
              : { borderLeft: `1px solid ${colors.border}`, paddingLeft: 24 }),
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: colors.texto.tertiary,
              }}
            >
              Créditos nuevos
            </span>
            <span
              data-testid="composicion-nota-clientes"
              title={NOTA_CLIENTES_GLOBAL}
              aria-label={NOTA_CLIENTES_GLOBAL}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: esMovil ? 16 : 13,
                height: esMovil ? 16 : 13,
                borderRadius: '50%',
                background: colors.ringTrack,
                color: colors.texto.tertiary,
                fontSize: esMovil ? 11 : 9.5,
                fontWeight: 800,
                cursor: 'help',
              }}
            >
              i
            </span>
            {estadoOk && !soloTre && <ChipSinMeta />}
          </div>

          <div data-testid="composicion-altas" style={renglon}>
            <span style={rotulo}>Clientes nuevos (altas)</span>{' '}
            <span style={cifra}>{conteo(totales.altas)}</span>
          </div>
          <div data-testid="composicion-estrenos" style={renglon}>
            <span style={rotulo}>Estrenaron su crédito</span>{' '}
            <span style={cifra}>{conteo(totales.estrenos)}</span>
          </div>

          {soloTre && <div style={subtitulo}>TRE no maneja crédito.</div>}

          {!estadoOk && (
            <div
              role="status"
              data-testid="composicion-generaciones-estado"
              style={{ ...subtitulo, color: colors.texto.secondary }}
            >
              {TEXTO_ESTADO[generaciones.estado as 'pendiente' | 'sin-snapshot']}
            </div>
          )}

          {lineaProcedencia && (
            <div data-testid="composicion-procedencia" style={{ ...subtitulo, fontWeight: 600 }}>
              {lineaProcedencia}
            </div>
          )}

          {avisoDesde && (
            <div
              role="note"
              data-testid="aviso-7c03"
              style={{
                marginTop: 2,
                background: '#fff6ed',
                border: '1px solid #f0d3b4',
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 12.5,
                lineHeight: 1.45,
                color: colors.texto.body,
              }}
            >
              Desde el {diaMes(avisoDesde)} casi no se abren cuentas 7MCV; las nuevas llevan 7C03 y
              no se cuentan hasta que Crédito lo decida.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ComposicionVenta;
